/**
 * /reports — the Reports area hub (persona round 2026-10: the CEO had no
 * report to look at). HR-admin only (company_admin + hr_manager).
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  BarChart3,
  IndianRupee,
  ReceiptText,
  Users,
  type LucideIcon,
} from "lucide-react";
import { ReportsShell } from "@/components/reports/reports-shell";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/reports/")({
  component: ReportsHub,
});

const TILES: readonly { label: string; href: string; icon: LucideIcon; blurb: string }[] = [
  {
    label: "Headcount",
    href: "/app/$tenantSlug/reports/headcount",
    icon: Users,
    blurb: "Active people by department, designation and location, plus who joined and who left in a period.",
  },
  {
    label: "Payroll Cost by Month",
    href: "/app/$tenantSlug/reports/payroll-cost",
    icon: IndianRupee,
    blurb: "Gross pay, deductions, employer PF and ESI, net pay and the total cost to the company — by department.",
  },
  {
    label: "Salary Register",
    href: "/app/$tenantSlug/reports/salary-register",
    icon: ReceiptText,
    blurb: "Every employee's pay for a locked month, line by line, ready for your accountant.",
  },
  {
    label: "HR Analytics",
    href: "/app/$tenantSlug/reports/analytics",
    icon: BarChart3,
    blurb: "Trends for headcount, attrition, leave, training, helpdesk and surveys.",
  },
];

function ReportsHub() {
  const { tenantSlug } = Route.useParams();
  return (
    <ReportsShell
      title="All Reports"
      description="Figures for the leadership team — every report downloads for Excel with amounts in rupees."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {TILES.map((t) => {
          const Icon = t.icon;
          return (
            <Link
              key={t.href}
              to={t.href}
              params={{ tenantSlug }}
              className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid={`reports-tile-${t.href.split("/").at(-1)}`}
            >
              <Card className="h-full transition-colors group-hover:border-primary/40">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                      <Icon aria-hidden className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="flex items-center justify-between gap-2 text-base">
                        {t.label}
                        <ArrowRight
                          aria-hidden
                          className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                        />
                      </CardTitle>
                      <CardDescription className="mt-1">{t.blurb}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            </Link>
          );
        })}
      </div>
    </ReportsShell>
  );
}
